import { Calendar, ChevronRight } from 'lucide-react'
import { useLocation } from 'wouter'
import { getImageUrls } from '@/utils/image-urls'

export interface NewsArticle {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  author?: number
}

interface NewsCardProps {
  article: NewsArticle
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '')
}

function truncate(text: string, maxLength: number): string {
  const stripped = stripHtml(text)
  if (stripped.length <= maxLength) return stripped
  return stripped.slice(0, maxLength) + '...'
}

export default function NewsCard({ article }: NewsCardProps) {
  const [, navigate] = useLocation()

  const handleClick = () => {
    navigate(`/news/${article.id}`)
  }

  const imageUrls = article.filename ? getImageUrls('news', article.filename) : null

  const formattedDate = article.published_date
    ? new Date(article.published_date).toLocaleDateString()
    : null

  const preview = article.content ? truncate(article.content, 200) : null

  return (
    <div
      onClick={handleClick}
      className="bg-[#3C3C3C] rounded-xl overflow-hidden hover:bg-[#4C4C4C] transition-colors cursor-pointer"
    >
      {/* Article Image */}
      {imageUrls && (
        <div className="w-full overflow-hidden">
          <img
            src={imageUrls.landscapeThumb}
            alt={article.filename_alt || article.heading}
            className="w-full h-auto"
          />
        </div>
      )}

      {/* Article Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Title */}
        <h3 className="text-[#ffffff] font-semibold text-lg mb-2 line-clamp-2">
          {article.heading}
        </h3>

        {/* Description */}
        {preview && (
          <p className="text-[#BABABB] text-sm mb-3 flex-1">
            {preview}
          </p>
        )}

        {/* Meta Info */}
        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center gap-4">
            {formattedDate && (
              <div className="flex items-center gap-1 text-[#888888] text-xs">
                <Calendar className="h-3 w-3" />
                <span>{formattedDate}</span>
              </div>
            )}
          </div>

          {/* Read More Arrow */}
          <div className="flex items-center text-[#EF4444]">
            <span className="text-sm font-medium hidden sm:inline">Read More</span>
            <ChevronRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </div>
  )
}
